// PR 0 — BattleScene mixin extraction (structural-only).
//
// The `playEvent` switch was the dominant chunk of BattleScene.ts. Extracted
// verbatim to keep that file readable as PRs 1–3 add new BattleEvent variants
// (`crit`, `miss`, `fail`, `status`, `cant`) + cinematic hooks. Zero behavior
// change: invoked via `.call(this, event)` from a one-line delegator on
// BattleScene, preserving the original `this`-binding semantics.
//
// `BattleScene` is imported type-only to avoid a runtime circular dependency
// (BattleScene.ts imports this module as a value to wire the delegator).
import type { BattleEvent } from '../battle/types';
import { SPECIES_TO_DEX } from '../battle/teamFromParty';
import { resolveActiveId } from '../battle/activeIdResolver';
import { runState } from '../state/runState';
import { saveManager } from '../state/saveManager';
import { careerStore } from '../state/careerStore';
import { getBaseExp } from '../data/baseExp';
import { computeExpGain } from '../data/expCurve';
import { audio } from '../systems/audio';
import { TRAINER_VICTORY_MUSIC } from '../data/audioMap';
import { RIVAL_BATTLE_REFS } from '../data/trainerParties';
import { FLAGS } from '../scripts/flagRegistry';
import { LOG_BEAT_MS, LEVEL_UP_BEAT_MS, MOVE_LINE_HOLD_MS, SHAKE_MS, SHAKE_INTENSITY, DESAT_MS, SCALE_PULSE_MS } from '../battle/pacing';
import { FLOAT_GUTTER_PX, FLOAT_EDGE_PX } from '../ui/battleFloatText';
import {
  resolveHitFx,
  formatDamageText,
  verdictsFor,
  verdictClass,
  VERDICT_LABEL,
  HITSTOP_MS,
} from '../battle/hitFxConfig';
import {
  isRivalSparringLoss,
  VAR_RIVAL_BATTLE_OUTCOME,
} from '../battle/battleRules';
import { playHitFx, hitStop } from '../battle/hitFx';
import {
  critText,
  missText,
  failText,
  statusText,
  cantText,
} from '../battle/cartridgeText';
import { loadGen3 } from '../data/dex';
import { statusTintHex, type PartyMemberStatus } from './../battle/statusVisuals';
import { effectsReduced } from '../render/motion';
import type {
  LearnedMovesModalResult,
  MoveDisplayData,
} from './LearnedMovesModal';
import type { BattleScene } from './BattleScene';
import { capitalize } from './BattleScene';

/** Apply status-condition Phaser tint to a sprite. Idempotent — clearing
 *  (status === null) restores the white "no tint" hex (0xffffff). Killed
 *  tweens prevent paralysis-flicker leaking past faint/switch. Eng review
 *  Issue 3A 2026-05-13: sprite?.active guard against post-destroy access;
 *  killTweensOf wipes any pending tweens on the same sprite. */
export function applyStatusTint(
  scene: BattleScene,
  sprite: Phaser.GameObjects.Image | undefined,
  status: PartyMemberStatus,
): void {
  if (!sprite || !sprite.active) return;
  scene.tweens.killTweensOf(sprite);
  sprite.setAlpha(1);
  sprite.setTint(statusTintHex(status));
  // Paralysis flicker: alpha 0.4 ↔ 1.0 over 600ms loop. Skipped under
  // reduced motion (the static tint already conveys the condition).
  if (status === 'par' && !effectsReduced()) {
    scene.tweens.add({
      targets: sprite,
      alpha: 0.4,
      duration: 300,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}

/** Resolve cartridge move metadata (name / type / power / accuracy / PP)
 *  for the LearnedMovesModal rows. Falls back to a status-shaped 0-power
 *  display if the dex lookup misses (network glitch / unknown move id). */
async function resolveMoveDisplay(id: string): Promise<MoveDisplayData> {
  try {
    const gen = await loadGen3();
    const data = gen.moves.get(id);
    if (data) {
      const accNum = typeof data.accuracy === 'number' ? data.accuracy : 100;
      return {
        id,
        name: data.name,
        type: (data.type ?? 'Normal').toLowerCase(),
        power: data.basePower ?? 0,
        accuracy: accNum,
        pp: data.pp ?? 0,
        maxPp: data.pp ?? 0,
      };
    }
  } catch {
    // Fall through to fallback.
  }
  return {
    id,
    name: capitalize(id),
    type: 'normal',
    power: 0,
    accuracy: 100,
    pp: 0,
    maxPp: 0,
  };
}

/** Resolve the cartridge display name for a side's currently-active
 *  Pokémon. Player active uses runState (id lookup via activeId);
 *  opponent active reads params.setup.p2.team[opponentFaints.length]
 *  — same heuristic the EXP attribution + faint-credit logic uses
 *  for "who is on the field right now". Falls back to "Pokemon" if
 *  the lookup misses (mid-teardown / stale state). */
function getSideName(scene: BattleScene, side: 'p1' | 'p2'): string {
  if (side === 'p1') {
    const member = scene.activeId
      ? runState.getParty().find((m) => m.id === scene.activeId)
      : undefined;
    return member ? capitalize(member.species) : 'Pokemon';
  }
  const idx = scene.opponentFaints.length;
  const opp = scene.params.setup.p2.team[idx];
  if (opp) return opp.name ?? capitalize(opp.species);
  return 'Pokemon';
}

export async function playEvent(this: BattleScene, event: BattleEvent): Promise<void> {
  switch (event.type) {
    case 'turn':
      this.appendLog(`— Turn ${event.turn} —`);
      await this.wait(400);
      // Release the choice gate now that the previous turn's animations
      // have played out and we've shown the new turn header.
      this.signalChoicesReady();
      break;
    case 'switch': {
      // First-turn switches log silently. Mid-battle switches: swap the
      // sprite texture, restore alpha + position (tweenFaint translated
      // it down + faded it out), and re-sync the HP bar to the actual
      // stored HP for the incoming Pokémon (sim preserves HP across
      // mid-battle switches; the bar must too).
      if (event.side === 'p1') {
        // v7: resolve the incoming active member's id via the
        // pendingSwitchToId bookkeeper (player-initiated switches) or
        // the species + non-fainted fallback (sim-initiated paths —
        // first |switch|p1a at battle start, Whirlwind/Roar in future
        // rosters, sim-internal forced switches racing the outgoing
        // command). Consume + clear the bookkeeper after use; activeId
        // is now the canonical key for HP / faint credit.
        const teamRefs = runState
          .getParty()
          .map((m) => ({ id: m.id, species: m.species }));
        const resolvedId = resolveActiveId(
          teamRefs,
          this.faintedIds,
          event.species,
          this.pendingSwitchToId,
        );
        if (resolvedId) this.activeId = resolvedId;
        this.pendingSwitchToId = null;
        // activeSlot mirror (informational only — no readers depend on
        // it post-v7, but kept for parity with the design doc).
        const partyIdx = runState
          .getParty()
          .findIndex((m) => m.id === this.activeId);
        if (partyIdx >= 0) this.activeSlot = partyIdx + 1;

        const newDexId = SPECIES_TO_DEX[event.species];
        if (newDexId !== undefined && this.playerSprite) {
          // Host seam (M2): BattleScene swaps to the 4x back-sprite
          // texture; the in-world duel host re-skins its OW battler
          // instead. Same guard, same downstream panel logic.
          this.applySwitchVisual('p1', event.species);

          // Read current HP from OUR hpById map. Sim's |request|
          // condition string after switch-in didn't reliably reflect
          // preserved HP, so we don't trust it. The map was populated
          // at battle start and is updated on every |damage|p1 event
          // under activeId, so the value here is authoritative.
          const stored = this.activeId ? this.hpById.get(this.activeId) : null;
          const hpPercent =
            stored && stored.max > 0
              ? Math.round((stored.current / stored.max) * 100)
              : 100;
          this.playerHpPercent = hpPercent;
          // Snap the HP bar to the new active's value — no animation
          // from the previous Pokémon's HP. The shell's CSS transition
          // re-engages on the next non-snap setHp() call (the next
          // |damage| event).
          this.playerStatusPanel?.setHp(hpPercent / 100, true);
          if (stored) {
            this.playerStatusPanel?.setHpNumeric(stored.current, stored.max);
          }
          // Switch may bring in a Pokémon at a different HP %. Re-
          // evaluate the cinematic gate.
          this.updateLastAliveCriticalState();
          // Type-tint the HP bar track to the active Pokémon's primary
          // type. @pkmn/dex is dynamic-imported; the chunk is warmed
          // at boot, so this resolves quickly. Fire-and-forget — the
          // tint flash-in (~one frame after switch-in) is acceptable.
          this.applyHpBarType('p1', event.species);

          // Update the status panel name + level for the new active.
          // request.team has the displayName + level; match by species
          // (no `s.active` filter — the request may still show the
          // OLD active when |switch| fires).
          const team = this.pendingRequest?.team ?? [];
          const slot = team.find((s) => s.species === event.species);
          if (slot) {
            this.playerStatusPanel?.setName(
              slot.displayName ?? capitalize(slot.species),
            );
            this.playerStatusPanel?.setLevel(slot.level ?? 1);
          }
          // Refresh the EXP bar fill for the new active species.
          this.refreshPlayerExp(event.species);
          // PR 3 EXP 5e: re-apply the new active's persistent status
          // tint so a switch-in to a paralyzed reserve preserves the
          // visual cue. activeMember is resolved via activeId (just
          // updated above via resolveActiveId).
          const activeMember = this.activeId
            ? runState.getParty().find((m) => m.id === this.activeId)
            : undefined;
          applyStatusTint(this, this.playerSprite, activeMember?.status ?? null);
          // Re-run layout so the sprite returns to its canonical
          // position (tweenFaint moved the sprite +32y and we'd
          // otherwise re-render the new active off-screen).
          this.layout();
        }
      } else if (event.side === 'p2') {
        // Wild battles only have one opponent in v0.1 so p2 |switch|
        // only fires once at battle start; trainers (Phase 12) will
        // need the same HP-from-request treatment but we don't have
        // p2 team data in pendingRequest today.
        const newDexId = SPECIES_TO_DEX[event.species];
        if (newDexId !== undefined && this.opponentSprite) {
          // Host seam (M2) — see the p1 branch note.
          this.applySwitchVisual('p2', event.species);
          this.opponentHpPercent = 100;
          this.opponentStatusPanel?.setHp(1.0, true);
          this.applyHpBarType('p2', event.species);
          // Look up level from setup.p2.team. Trainers with multiple
          // same-species opponents (Doug's 3 Weedles all L7) collide on
          // first match, but they're all the same level so it works.
          // Wild battles only have one opponent.
          const oppMatch = this.params.setup.p2.team.find(
            (m) => m.species === event.species,
          );
          this.opponentStatusPanel?.setName(
            oppMatch?.name ?? capitalize(event.species),
          );
          this.opponentStatusPanel?.setLevel(oppMatch?.level ?? 1);
          this.layout();
        }
      }
      break;
    }
    case 'move': {
      const display = event.pokemon || (event.side === 'p1' ? 'Player' : 'Opponent');
      this.appendLog(`${display} used ${event.moveName}!`);
      // Combat juice: resolve the move's TYPE for the hit-FX skin. The
      // gen-3 dex is preloaded at battle mount, so this await is a
      // cache hit in practice; a miss just falls back to the normal
      // skin at damage time.
      try {
        const gen = await loadGen3();
        this.lastMoveType[event.side] =
          gen.moves.get(event.moveName)?.type?.toLowerCase() ?? null;
      } catch {
        this.lastMoveType[event.side] = null;
      }
      // Cartridge rhythm (playtest round 2): the line holds FIRST, the
      // attack follows. The impact (|-damage|) lands right after the
      // lunge with no dead wait between them.
      await this.wait(MOVE_LINE_HOLD_MS);
      await this.tweenAttacker(event.side);
      break;
    }
    case 'damage': {
      // Hit SFX. Latched-effectiveness from the preceding -supereffective/
      // -resisted/-immune line decides which cue plays:
      //   super → hit_super (heavier, the cartridge "boom")
      //   resisted/immune → no SFX (silence reads as "did nothing")
      //   neutral → hit_normal (default)
      // The latch is consumed here; subsequent damage events default to
      // neutral until sim emits another effectiveness marker.
      const eff = this.nextEffectiveness[event.target];
      this.nextEffectiveness[event.target] = null;
      const crit = this.nextCrit[event.target];
      this.nextCrit[event.target] = false;
      // ── Combat juice (playtest 2026-08-02): type-keyed cartridge
      // particles + hit-stop + floating text, all fired synchronously
      // with the impact (master-timeline rule: feedback ON the damage
      // event, drain follows underneath). Reduced motion: a single
      // white tint flash carries the impact; text spawns via the CSS
      // hold-and-fade variant; no particles, no hit-stop.
      {
        const defender =
          event.target === 'p1' ? this.playerSprite : this.opponentSprite;
        const attacker = event.target === 'p1' ? 'p2' : 'p1';
        const plan = resolveHitFx(this.lastMoveType[attacker], eff, crit);
        const center = defender.getCenter();
        // Host seam (M2): DOM float anchors need SCREEN px. In
        // BattleScene the camera never zooms/scrolls so toScreen is
        // the identity and screenScale is 1; the in-world duel host
        // projects through the punched-in world camera. Particle FX
        // (playHitFx) deliberately keep world-space `center` — they
        // render in-scene through whatever camera is live.
        const screenCenter = this.toScreen(center.x, center.y);
        const screenHalfW = (defender.displayWidth / 2) * this.screenScale();
        // 2-frame flash: snapshot tint state so a persistent status
        // tint (psn/par/...) is restored, not wiped. Used by the
        // reduce-animations path AND as the texture-load-failure
        // fallback so an impact never reads as nothing.
        const flashDefender = (): void => {
          const hadTint = defender.isTinted;
          const priorTint = defender.tintTopLeft;
          defender.setTintFill(0xffffff);
          this.time.delayedCall(90, () => {
            if (hadTint) defender.setTint(priorTint);
            else defender.clearTint();
          });
        };
        if (effectsReduced()) {
          if (eff !== 'immune') flashDefender();
        } else if (plan) {
          if (plan.hitStop) hitStop(this, HITSTOP_MS);
          if (!playHitFx(this, center.x, center.y, plan, this.hitFxScale())) {
            flashDefender();
          }
        }
        // Floating text — damage figure from the scene's own HP model
        // (percent delta × sim max), verdict pills player-perspective.
        const before =
          event.target === 'p1' ? this.playerHpPercent : this.opponentHpPercent;
        const prevAbs = Math.round((before / 100) * event.hpMax);
        const dmgText = formatDamageText(
          Math.max(0, prevAbs - event.hpCurrent),
          before - event.hpPercent,
        );
        const tier = plan?.tier ?? 'neutral';
        // Floats anchor OUTBOARD of the sprite — right of the player's
        // (bottom-left of stage), left of the enemy's (top-right) —
        // never over the pixel art (playtest round 2: "plenty of space
        // to the right of mine and the left of the enemy's"). Clamped
        // to the canvas with a small margin.
        const outboard =
          event.target === 'p1'
            ? screenCenter.x + screenHalfW + FLOAT_GUTTER_PX
            : screenCenter.x - screenHalfW - FLOAT_GUTTER_PX;
        const anchorX = Math.max(
          FLOAT_EDGE_PX,
          Math.min(this.scale.width - FLOAT_EDGE_PX, outboard),
        );
        if (dmgText) {
          this.battleFloatText?.spawnDamage(anchorX, screenCenter.y - 6, dmgText, tier);
        }
        verdictsFor(eff, crit).forEach((kind, i) => {
          this.time.delayedCall(140 * (i + 1), () => {
            this.battleFloatText?.spawnVerdict(
              anchorX,
              screenCenter.y + 18 + i * 20,
              VERDICT_LABEL[kind],
              verdictClass(kind, event.target),
            );
          });
        });
      }
      if (eff === 'super') {
        audio.playSfx('hit_super');
        // PR 2 / EXP 2 pixel boom — camera shake + targeted desat fire
        // SYNCHRONOUSLY with the damage event, not after the 1000ms HP-
        // drain await (eng review Issue 5A 2026-05-13). The boom lands
        // on impact; the HP drain follows underneath.
        this.cameras.main.shake(SHAKE_MS, SHAKE_INTENSITY);
        const gameEl = document.getElementById('game');
        gameEl?.classList.add('is-super-effective');
        // Cleanup is JS-driven so the CSS transition can ease back to
        // saturate(1). DESAT_MS = 250ms matches DESIGN.md cinematic spec.
        this.time.delayedCall(DESAT_MS, () => {
          gameEl?.classList.remove('is-super-effective');
        });
      } else if (eff === null) audio.playSfx('hit_normal');
      // resisted / immune → silence on purpose
      const handle = event.target === 'p1' ? this.playerStatusPanel : this.opponentStatusPanel;
      const before = event.target === 'p1' ? this.playerHpPercent : this.opponentHpPercent;
      const after = event.hpPercent;
      if (event.target === 'p1') {
        this.playerHpPercent = after;
        // v7: write the damage to hpById under the active member's id
        // (replaces species-keyed writes). The |damage| event's
        // hpCurrent/hpMax are sim's authoritative post-damage values
        // for the active battler; activeId is kept current by the
        // |switch| handler above.
        if (this.activeId) {
          this.hpById.set(this.activeId, {
            current: event.hpCurrent,
            max: event.hpMax,
          });
        }
        // Sync the status panel numeric so "18 / 25" tracks each hit.
        this.playerStatusPanel?.setHpNumeric(event.hpCurrent, event.hpMax);
        // Re-evaluate the last-alive cinematic gate after each
        // damage — crossing the 10 % HP threshold latches the
        // cinematic on/off.
        this.updateLastAliveCriticalState();
      } else {
        this.opponentHpPercent = after;
      }
      // Cinematic effects scale with damage magnitude per design doc
      // rev 6's "Damage-intensity signal". > 50 % single-hit damage
      // gets a red-overlay flash BEFORE drain begins; > 30 % adds a
      // bar-container shake; sweep speed scales (slow / normal / fast).
      const magnitude = Math.max(0, (before - after) / 100);
      if (handle) {
        if (magnitude > 0.5) handle.triggerFlash();
        if (magnitude > 0.3) handle.triggerShake();
        const sweepSpeed: 'slow' | 'normal' | 'fast' =
          magnitude < 0.1 ? 'slow' : magnitude > 0.3 ? 'fast' : 'normal';
        handle.triggerSweep(sweepSpeed);
        handle.setHp(after / 100);
      }
      // Wait for the CSS drain transition (1000 ms) before the next
      // battle event fires. Matches the legacy tweenHpBar's await.
      await this.wait(1000);
      // Cartridge order: the verdict LINES land after the drain, each
      // on its own beat — "A critical hit!" then "It's super
      // effective!" — so every half-turn ends readable instead of the
      // lines racing the impact (playtest round 2). The pills already
      // flashed at impact; these are the textual record.
      if (crit) {
        this.appendLog(critText());
        await this.wait(LOG_BEAT_MS);
      }
      if (eff === 'super') {
        this.appendLog("It's super effective!");
        await this.wait(LOG_BEAT_MS);
      } else if (eff === 'resisted') {
        this.appendLog("It's not very effective...");
        await this.wait(LOG_BEAT_MS);
      }
      break;
    }
    case 'effectiveness': {
      // Latch only — the LINE prints after the HP drain, matching the
      // cartridge order (used → attack → drain → "It's super
      // effective!"). Protocol emits these markers BEFORE |-damage|;
      // printing them there is what made turns read as mush (playtest
      // round 2). Exception: immune has NO following damage event, so
      // its line prints here or never.
      this.nextEffectiveness[event.target] = event.kind;
      if (event.kind === 'immune') {
        this.appendLog(`It doesn't affect ${getSideName(this, event.target)}...`);
        await this.wait(LOG_BEAT_MS);
      }
      break;
    }
    case 'crit': {
      // Latch only — feeds hit-stop + the CRITICAL pill at impact; the
      // "A critical hit!" line prints post-drain (cartridge order).
      this.nextCrit[event.target] = true;
      break;
    }
    case 'miss': {
      this.appendLog(missText(getSideName(this, event.source)));
      await this.wait(LOG_BEAT_MS);
      break;
    }
    case 'fail': {
      this.appendLog(failText());
      await this.wait(LOG_BEAT_MS);
      break;
    }
    case 'status': {
      this.appendLog(statusText(getSideName(this, event.target), event.status));
      // PR 3 EXP 5e: in-battle sprite tint per condition. Paralysis adds
      // a flicker tween (alpha yoyo); static tint for the rest. Cleared
      // automatically on faint (sprite alpha fades to 0 in tweenFaint)
      // and re-applied per side on switch + battle start (below).
      const sprite =
        event.target === 'p1' ? this.playerSprite : this.opponentSprite;
      applyStatusTint(this, sprite, event.status);
      await this.wait(LOG_BEAT_MS);
      break;
    }
    case 'cant': {
      this.appendLog(cantText(getSideName(this, event.target), event.reason));
      await this.wait(LOG_BEAT_MS);
      break;
    }
    case 'faint': {
      const fainterSide = event.target;
      const display = event.pokemon || (fainterSide === 'p1' ? 'Player' : 'Opponent');
      // v7: permadeath credit goes to activeId — the |switch|p1a handler
      // updates activeId via resolveActiveId on every player-side switch,
      // so by the time |faint|p1 fires, activeId already names the
      // instance taking the hit. The pre-v7 path used a species-keyed
      // findIndex over setup.p1.team, which silently picked the wrong
      // slot when two same-species Pokémon coexisted (the user-reported
      // "second Pikachu inherited the first one's HP" bug). Race-free
      // even mid-turn — switches resolve into activeId before faints
      // are emitted.
      if (fainterSide === 'p1') {
        if (this.activeId) {
          this.faintedIds.add(this.activeId);
          this.playerFainted = true;
          // Zero the HP entry. battleEnd will splice this id from
          // runState anyway, but zeroing keeps mid-battle queries
          // (re-render, switch menu) honest about what's left.
          const stored = this.hpById.get(this.activeId);
          if (stored) stored.current = 0;
          // Step 7: stamp the death onto the careerStore entry by id.
          // diedTo = the opponent's CURRENT active species (the killer
          // of record); sim's RandomPlayerAI sends p2 in team order so
          // setup.p2.team[opponentFaints.length] is the live one when
          // the player's slot fell (the same heuristic the EXP
          // attribution uses below for killer credit).
          const fainterId = this.activeId;
          const fainter = runState
            .getParty()
            .find((m) => m.id === fainterId);
          const killer =
            this.params.setup.p2.team[this.opponentFaints.length]?.species ??
            this.params.setup.p2.team[0]?.species ??
            '?';
          // Round-4 cartridge law: the rival battle is sparring —
          // faints there are never career deaths (battleEnd's
          // matching guard skips the splice; the starter walks out
          // fully healed). Every other battle records as before.
          const sparring =
            !!this.params.trainerScriptRef &&
            RIVAL_BATTLE_REFS.has(this.params.trainerScriptRef);
          if (!sparring) {
            careerStore.markDied({
              id: fainterId,
              diedTo: killer,
              diedZone: this.params.battleZone,
              levelAtDeath: fainter?.level ?? 0,
            });
          }
        }
      } else {
        // EXP attribution: snapshot the opponent's level (p2.team is
        // sent in team order by sim's AI; the Nth p2 faint = p2.team[N])
        // and the killer-of-record (current active p1 id, so duplicates
        // resolve to the slot that actually made the KO).
        const opponentIdx = this.opponentFaints.length;
        const opponent = this.params.setup.p2.team[opponentIdx];
        const killerId = this.activeId ?? '';
        if (opponent) {
          this.opponentFaints.push({
            species: opponent.species,
            level: opponent.level,
            killerId,
          });
        }
      }
      audio.playSfx('faint');
      const sprite = fainterSide === 'p1' ? this.playerSprite : this.opponentSprite;
      if (fainterSide === 'p1') {
        // Player faint = the run's emotional load-bearing moment.
        // Run the full cinematic (HP red pulse + type-tint drain +
        // canvas saturate + dialogue) in parallel with the sprite
        // drop, then wait for both to land before yielding back to
        // the sim's event loop.
        // Prefer the active member's species (a properly-capitalized
        // canonical name) over event.pokemon (sim's display name
        // which may be a numeric placeholder for unnamed parties).
        const activeMember = this.activeId
          ? runState.getParty().find((m) => m.id === this.activeId)
          : undefined;
        const lostName = activeMember
          ? capitalize(activeMember.species)
          : event.pokemon || 'Pokemon';
        await Promise.all([
          this.tweenFaint(sprite),
          this.runPlayerFaintCinematic(lostName),
        ]);
      } else {
        // Opponent faint. Standard non-final faints stay terse (log
        // line + sprite drop); the FINAL opponent faint (the one that
        // ends the battle) gets the desat-to-gray death fade per
        // design ruling 2026-05-13 Decision 3 — opponent sprite
        // desaturates to gray + alpha 1.0 → 0, replacing tweenFaint.
        //
        // The VICTORY hero text that used to chain off this used to
        // play here AS PART OF the cinematic, but moved to battleEnd
        // post-EXP-loop as the FINAL beat — user feedback that the
        // hero firing here led to "still looking at combat" after it
        // faded. Hero is now the climactic last frame before exit.
        const isLastOpponent =
          this.opponentFaints.length >= this.params.setup.p2.team.length;
        this.appendLog(`${display} fainted!`);
        if (isLastOpponent) {
          await this.runOpponentDeathFade(sprite);
        } else {
          await this.tweenFaint(sprite);
        }
      }
      break;
    }
    case 'battleEnd': {
      // Release any pending waitForChoicesReady — the battle is ending
      // so no more prompts will be matched to a 'turn' event. The choice
      // loop sees battleOver=true and exits cleanly.
      this.battleOver = true;
      this.signalChoicesReady();
      // v7: flush hpById → runState BEFORE the permadeath splice. Skip
      // fainted ids — the splice removes them next anyway. Shared with
      // the runFromBattle exit path so neither can drift out of sync.
      this.flushSurvivorHpToRunState();
      // PR 3 EXP 5b: also flush sim-side status conditions for every
      // non-fainted member. Same pre-splice ordering — the splice
      // removes fainted ids next; we don't write status for them.
      if (this.battle) this.flushBattleStatusToRunState(this.battle);

      // EXP awards (after HP flush so level-up's full-heal is the final
      // word on a survivor's HP — without this ordering, the HP flush
      // would overwrite the leveled-up HP with mid-battle stored HP).
      // Skipped on loss/draw (no EXP for failures). Cartridge convention:
      // if the killer also fainted in this battle, their EXP is forfeit.
      if (event.winner === 'p1') {
        const isTrainer = !!this.params.trainerScriptRef;
        for (const f of this.opponentFaints) {
          // v7: look up the killer by id. addExp is still index-based,
          // so we resolve the index from the live party AFTER the HP
          // flush (party order is stable until removeWhere splices it
          // later in this block). Duplicates resolve to the correct
          // slot now — the species heuristic would credit the lower-
          // slot one even when the higher-slot one made the KO.
          if (!f.killerId) continue;
          if (this.faintedIds.has(f.killerId)) continue;
          const killerIdx = runState
            .getParty()
            .findIndex((m) => m.id === f.killerId);
          if (killerIdx < 0) continue;
          let amount = 0;
          try {
            amount = computeExpGain(getBaseExp(f.species), f.level, isTrainer);
          } catch (err) {
            // Unknown species in baseExp table — surfaces as a thrown
            // Error. Log and skip rather than blocking battleEnd; this
            // is a content gap, not a runtime failure.
            console.warn('[battle] EXP skipped:', err);
            continue;
          }
          const killerSpecies = runState.getParty()[killerIdx]?.species ?? '';
          const levelUps = await runState.addExp(killerIdx, amount);
          audio.playSfx('exp_gain');
          this.appendLog(`${capitalize(killerSpecies)} gained ${amount} EXP.`);
          // Refresh the in-battle EXP readout so the player visibly sees
          // their pool fill up. If a level-up fires the same beat
          // refreshes the counter against the new threshold (req for L+1).
          this.refreshPlayerExp(killerSpecies);
          // The EXP chime lands as its own beat before any jingle — and
          // paces multi-KO iterations apart. Without this, exp_gain +
          // level_up + victory music all started in one frame and the
          // level-up fanfare was inaudible (first-friend playtest
          // 2026-08-02).
          await this.wait(LOG_BEAT_MS);
          // Count overflow events in this chain so the modal's "Move N of M"
          // progress indicator can render. Single-overflow chains pass null
          // (indicator hides).
          const overflowEvents = levelUps.filter((e) => e.requiresChoice);
          let overflowSeen = 0;
          for (const ev of levelUps) {
            // One jingle + pulse per level-up CHAIN (the first event) —
            // a 3x-EXP multi-level chain logs its later levels at
            // LOG_BEAT_MS without re-triggering the 2.35s jingle.
            const isChainHead = ev === levelUps[0];
            if (isChainHead) audio.playSfx('level_up');
            // PR 2 / EXP 3: sprite scale pulse 1.0 → 1.15 → 1.0 over
            // SCALE_PULSE_MS. Fires only when the leveling species is
            // the active battler — bench level-ups can't pulse a sprite
            // that isn't on screen, but the orb badge tick (PartyOrbs
            // diff-on-render) still fires for them on next WAKE.
            const activeMember = this.activeId
              ? runState.getParty().find((m) => m.id === this.activeId)
              : undefined;
            if (
              isChainHead &&
              activeMember &&
              activeMember.species === ev.species &&
              this.playerSprite
            ) {
              // Host seam (M2): pulse from the host's resting scale —
              // 4x battle stills in BattleScene, 1x OW walker in the
              // in-world duel host. The yoyo returns to `from`, so a
              // hardcoded SPRITE_SCALE would strand the walker at 4x.
              const base = this.battlerBaseScale();
              this.tweens.add({
                targets: this.playerSprite,
                scale: { from: base, to: base * 1.15 },
                duration: SCALE_PULSE_MS / 2,
                yoyo: true,
                ease: 'Quad.easeInOut',
              });
            }
            this.appendLog(`${capitalize(ev.species)} grew to Lv ${ev.level}!`);
            for (const move of ev.newMoves) {
              // Dex display name, not the raw sim id — 'Vine Whip', never
              // 'VINEWHIP' (second playtest). Matches the forgot/learned
              // lines in the overflow path below.
              const display = await resolveMoveDisplay(move);
              this.appendLog(`${capitalize(ev.species)} learned ${display.name}!`);
            }
            this.refreshPlayerExp(ev.species);
            // PR 1: slot-full overflow → cartridge "wants to learn" ceremony.
            // sim has finished; battleEnd is mid-handler. The await pauses
            // the JS loop, not the sim — no deadlock risk per brainstorm
            // Premise 4. Modal SHUTDOWN resolves with {forgetIndex: null}
            // so a tab-close mid-modal can't hang this promise (eng review
            // Issue 1A 2026-05-13).
            if (ev.requiresChoice && ev.pendingMove) {
              overflowSeen += 1;
              const member = runState.getParty()[ev.partyIndex];
              const currentIds = member?.moves ?? [];
              const [currentMoves, pendingMove] = await Promise.all([
                Promise.all(currentIds.map((id) => resolveMoveDisplay(id))),
                resolveMoveDisplay(ev.pendingMove),
              ]);
              const progress =
                overflowEvents.length > 1
                  ? { current: overflowSeen, total: overflowEvents.length }
                  : null;
              const result = await new Promise<LearnedMovesModalResult>(
                (resolve) => {
                  this.scene.launch('LearnedMovesModal', {
                    pokemonName: capitalize(ev.species),
                    currentMoves,
                    pendingMove,
                    progress,
                    onResolve: resolve,
                  });
                },
              );
              runState.applyMoveChoice(
                ev.partyIndex,
                result.forgetIndex,
                ev.pendingMove,
              );
              if (result.forgetIndex !== null) {
                const oldName =
                  currentMoves[result.forgetIndex]?.name ??
                  capitalize(currentIds[result.forgetIndex] ?? 'move');
                this.appendLog(
                  `${capitalize(ev.species)} forgot ${oldName} and learned ${pendingMove.name}!`,
                );
              } else {
                this.appendLog(
                  `${capitalize(ev.species)} did not learn ${pendingMove.name}.`,
                );
              }
              await this.wait(LOG_BEAT_MS);
            } else {
              // Dedicated fanfare window: the chain head holds
              // LEVEL_UP_BEAT_MS so the jingle + pulse are heard/seen
              // before the next log line — and before the victory
              // music + hero, which are sequenced after this loop.
              // Later chain events pace at the standard beat. The
              // overflow branch above needs no window — the modal
              // interaction dwarfs the jingle.
              await this.wait(isChainHead ? LEVEL_UP_BEAT_MS : LOG_BEAT_MS);
            }
          }
        }
      }
      // Round-4 cartridge law: the lab rival battle is SPARRING —
      // permadeath starts when the world opens. A wipe here must not
      // splice the starter, must not write a career death (see the
      // faint case's matching guard), and resolves the encounter like
      // a win, with the gloat dialogue variant.
      const rivalSparringLoss = isRivalSparringLoss(
        this.params.trainerScriptRef,
        event.winner,
      );
      // v7: capture casualties BEFORE the permadeath splice so
      // EndRunScene's defeat-mode graveyard list is complete even
      // though runState.party will be empty by the time the scene
      // reads it. Iterate the LIVE party by id (party + faintedIds
      // both still intact at this point) — species + level come from
      // the PartyMember entry that's about to be spliced.
      const casualties: Array<{ species: string; level: number }> = [];
      if (!rivalSparringLoss) {
        for (const m of runState.getParty()) {
          if (this.faintedIds.has(m.id)) {
            casualties.push({ species: m.species, level: m.level });
          }
        }
        // Permadeath splice — shared with the runFromBattle exit path so
        // neither can drift out of sync. Logs "X has been lost." per fainted
        // member, then runState.removeWhere fires onMembersRemoved which the
        // orb strip subscribes to for the tombstone cascade.
        this.spliceFaintedFromRunState();
      }
      // Phase 12 M4: trainer defeat flag. If this was a trainer-mode
      // battle and the player won, mark the trainer as defeated so
      // their LOS check no longer fires on subsequent steps. Lost or
      // drew → leave the flag unset, trainer will re-engage next pass.
      if (event.winner === 'p1' && this.params.trainerScriptRef) {
        runState.markDefeatedTrainer(this.params.trainerScriptRef);
        // PR A4: if the defeated trainer was the Pallet-lab rival, also
        // set RIVAL_BATTLE_RESOLVED so the WorldScene WAKE handler
        // dispatches the rival post-battle cinematic ("Smell you
        // later!" + walks south + exits). Cleared at handler-end.
        if (RIVAL_BATTLE_REFS.has(this.params.trainerScriptRef)) {
          runState.setFlag(FLAGS.RIVAL_BATTLE_RESOLVED);
          runState.setVar(VAR_RIVAL_BATTLE_OUTCOME, 'win');
        }
      }
      if (rivalSparringLoss) {
        // The blackout-at-Mom's equivalent: full heal (HP + status),
        // nothing died, and the WAKE cinematic plays the gloat variant.
        // Resolves identically to a win — only FLEE leaves the
        // encounter open.
        runState.healAll();
        runState.setFlag(FLAGS.RIVAL_BATTLE_RESOLVED);
        runState.setVar(VAR_RIVAL_BATTLE_OUTCOME, 'loss');
      }
      saveManager.save();

      const isTrainer = !!this.params.trainerScriptRef;
      // Phase 15a: victory fanfare on p1 wins; silence on loss/draw
      // (DESIGN.md: "permadeath = somber, not celebratory" — no defeat
      // music). Trainer/Brock get their override; wild gets victory_wild.
      if (event.winner === 'p1') {
        const ref = this.params.trainerScriptRef;
        const victoryKey = ref
          ? (TRAINER_VICTORY_MUSIC[ref] ?? 'victory_trainer')
          : 'victory_wild';
        void audio.playMusic(this, victoryKey, { fadeMs: 200 });
      } else {
        audio.stopMusic(this, { fadeMs: 1500 });
      }

      // Phase 12 M5: end-of-run conditions. Two exit paths:
      //   1. Brock defeated → win mode
      //   2. Party fully wiped (regardless of opponent) → defeat mode
      //      (permadeath rule from review Issue 4: total wipe = run over,
      //       Reset Run forced)
      // Otherwise → normal exit back to overworld.
      const brockDefeated =
        event.winner === 'p1' &&
        this.params.trainerScriptRef === 'PewterCity_Gym_EventScript_Brock';
      const fullWipe = runState.getParty().length === 0;

      if (event.winner === 'p1') {
        // VICTORY hero text is the FINAL cathedral beat — fires AFTER
        // the EXP loop (which already ran above) so the hero is the
        // climactic last frame before the scene exit fade. Skips the
        // legacy "Player won!" log line + 2200ms wait — the hero IS
        // the win announcement, anything else would loiter on the
        // combat scene post-cathedral (user feedback 2026-05-13).
        await this.runVictoryHero();
        if (brockDefeated) {
          this.exitToEndRun('win', casualties);
        } else {
          this.exitToWorld();
        }
      } else if (rivalSparringLoss) {
        // Sparring loss — no blackout, no EndRun, nobody died. The
        // brief somber beat plays, then straight back to the lab where
        // the WAKE cinematic delivers the gloat + exit.
        this.appendLog(`You lost to ${this.params.setup.p2.name}.`);
        await this.wait(2200);
        this.exitToWorld();
      } else if (fullWipe) {
        // Loss with full party wipe — run the BLACKOUT defeat cinematic
        // before EndRunScene. The "X was defeated." log line + 2200ms
        // wait stay here as the somber beat that frames the BLACKOUT
        // (cartridge convention; no hero replacing them on the loss
        // path, only on wins).
        const defeatMsg = isTrainer
          ? `You lost to ${this.params.setup.p2.name}.`
          : `${this.params.setup.p1.name} was defeated.`;
        this.appendLog(defeatMsg);
        await this.wait(2200);
        await this.runDefeatCinematic();
        this.exitToEndRun('defeat', casualties);
      } else {
        // Draw, or a loss without full wipe (rare — e.g. run-from-battle
        // raced an opponent KO). Keep the standard log + brief pause +
        // exit, no hero on either side.
        const drawMsg = event.winner === 'draw'
          ? 'It was a draw.'
          : isTrainer
            ? `You lost to ${this.params.setup.p2.name}.`
            : `${this.params.setup.p1.name} was defeated.`;
        this.appendLog(drawMsg);
        await this.wait(2200);
        this.exitToWorld();
      }
      break;
    }
    case 'message':
      this.appendLog(event.text);
      await this.wait(700);
      break;
  }
}
